import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreContext';
import Button from '../../components/Button/Button';
import SetupPreview from './SetupPreview';
import SetupTalk from './SetupTalk';
import { setupHasDraft } from './setupDraft';
import { useStoreSetupChat } from './useStoreSetupChat';
import './CreateStore.scss';

/**
 * Création d'un store : le brief se discute, l'aperçu se remplit, puis on crée.
 */

const CreateStore: React.FC = () => {
  const navigate = useNavigate();
  const { createStore, isLoading, error, clearError } = useStore();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [contact, setContact] = useState({
    description: '',
    address: '',
    phone: '',
    email: ''
  });
  const [contactErrors, setContactErrors] = useState({ phone: '', email: '' });

  const chat = useStoreSetupChat({
    onStoreName: (value) => {
      setName(value);
      setNameError('');
    }
  });

  const onContact = (field: keyof typeof contact, value: string) => {
    setContact((current) => ({ ...current, [field]: value }));
    if (field === 'phone' || field === 'email') {
      setContactErrors((current) => ({ ...current, [field]: '' }));
    }
    if (error) clearError();
  };

  const validate = () => {
    let ok = true;
    if (!name.trim() || name.trim().length < 2) {
      setNameError('Le nom doit contenir au moins 2 caractères');
      ok = false;
    } else {
      setNameError('');
    }

    const phone = contact.phone.replace(/\s/g, '');
    const next = { phone: '', email: '' };
    if (phone && !/^[0-9]{10,15}$/.test(phone)) {
      next.phone = '10 à 15 chiffres';
      ok = false;
    }
    if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
      next.email = 'Format d’e-mail invalide';
      ok = false;
    }
    setContactErrors(next);
    return ok;
  };

  const handleCreate = async () => {
    if (!validate() || isLoading || chat.beat?.phase === 'think') return;
    try {
      await createStore({
        name: name.trim(),
        description: contact.description,
        address: contact.address,
        phone: contact.phone.replace(/\s/g, ''),
        email: contact.email,
        ...(setupHasDraft(chat.setup) ? { setup: chat.setup } : {})
      });
      navigate('/orders');
    } catch (err) {
      console.error('Create store error:', err);
    }
  };

  return (
    <div className="store-setup">
      <header className="store-setup__top">
        <Link to="/stores" className="store-setup__back">Stores</Link>
        <p className="store-setup__kicker">Mise en route</p>
        <div className="store-setup__name">
          <label htmlFor="store-name">Nom</label>
          <input
            id="store-name"
            value={name}
            placeholder="Mon store"
            onChange={(event) => {
              setName(event.target.value);
              if (nameError) setNameError('');
              if (error) clearError();
            }}
          />
          {nameError ? <small>{nameError}</small> : null}
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={handleCreate}
          loading={isLoading}
          disabled={chat.beat?.phase === 'think'}
        >
          Créer
        </Button>
      </header>

      {error ? <p className="store-setup__error">{error}</p> : null}

      <div className="store-setup__body">
        <SetupTalk
          messages={chat.messages}
          thinking={chat.beat?.phase === 'think'}
          onSend={chat.send}
        />
        <SetupPreview
          name={name}
          setup={chat.setup}
          shown={chat.shown}
          beat={chat.beat}
          included={chat.included}
          contact={contact}
          contactErrors={contactErrors}
          onContact={onContact}
        />
      </div>
    </div>
  );
};

export default CreateStore;
